import { Button, CloseButton } from '@ui/shared/Button';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { Subscription } from 'rxjs';
import styled from 'styled-components';
import {
  closePopup,
  openPopup,
} from '../../../../data/providers/popupsProvider';
import { tagsProvider } from '../../../../data/providers/tagsProvider';
import {
  IAthlete,
  ISelectableEntity,
  ITag,
  ITest,
} from '../../../../data/types';
import {
  bulkActivate,
  bulkChangeAthlete,
  bulkDisable,
  bulkRetagTests,
} from '../../../../data/writers/test';
import { IUpdateInPlace } from '../../../../hooks/Tests/useInfiniteTests';
import { FullHeightColumn } from '../../../layout/Column';
import { Row } from '../../../layout/Row';
import { ScrollableArea } from '../../../layout/ScrollableArea';
import { CardBody } from '../../../shared/card/CardBody';
import {
  CardFooter,
  CardFooterLeft,
  CardFooterRight,
} from '../../../shared/card/CardFooter';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../../shared/card/CardHeader';
import { Count } from '../../../shared/Count';
import { ControlLabel } from '../../../shared/form/ControlLabel';
import { ErrorMessage } from '../../../shared/form/ErrorMessage';
import { ListControlLabel } from '../../../shared/form/ListControlLabel';
import { AthleteSelect } from '../../../shared/form/selects/AthleteSelect';
import { ListControlContainer } from '../../../shared/List/ListControlContainer';
import { LinearLoading } from '../../../shared/loading/LinearLoading';
import { PopupCard, PopupCardContent } from '../../../shared/popups/PopupCard';
import { TabLabel, TabLabels } from '../../../shared/tabs/Tabs';
import { ISelectedTests } from '../ExportList';
import { SelectedTestList } from './SelectedTestList';
import { TagList } from './TagList';

enum EDIT_MODE {
  ATHLETE = 0,
  TAGS = 1,
  DELETE = 2,
}

enum ERROR_MESSAGES {
  GENERIC = 'An error occured while processing the changes.',
}

const tabsElevation = 8;

interface IPopupProps {
  clearSelectedTests: () => void;
  selectedTests: ISelectedTests;
  onClose: () => void;
  updateTests: (updateToApply: IUpdateInPlace) => void;
  showingDisabledTests: boolean;
}

export interface ISelectedTagMap {
  [tagId: string]: {
    state: boolean;
    tag: ITag;
  };
}

const EditTestsPopup = (props: IPopupProps): React.ReactElement => {
  const [mode, setMode] = React.useState<EDIT_MODE>(EDIT_MODE.ATHLETE);
  const [tags, setTags] = React.useState([]);
  const [selectedTags, setSelectedTags] = React.useState<ISelectedTagMap>({});
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [selectedAthlete, setSelectedAthlete] =
    React.useState<ISelectableEntity>();
  const [errorMessage, setErrorMessage] = React.useState<ERROR_MESSAGES>();

  /**
   * toggles a tags selected state
   *
   * @param tag
   * @param action
   */
  const handleTagChange = (tag: ITag, action: 'add' | 'remove'): void => {
    const nextTags = { ...selectedTags };
    if (action === 'remove') {
      delete nextTags[tag.id];
    } else {
      nextTags[tag.id] = {
        state: true,
        tag,
      };
    }
    setSelectedTags(nextTags);
  };

  /**
   * subscribe to tags
   */
  React.useEffect((): (() => void) => {
    const subscriber: Subscription = tagsProvider().subscribe(
      (mTags: ITag[]): void => {
        setTags(mTags);
      }
    );
    return (): void => {
      subscriber.unsubscribe();
    };
  }, []);

  /**
   * processes the changes to tags
   */
  const processTagChanges = async (): Promise<void> => {
    setIsProcessing(!isProcessing);
    setErrorMessage(null);
    try {
      await bulkRetagTests(
        props.selectedTests.tests,
        Object.keys(selectedTags)
      );
      props.updateTests({
        selectedTests: props.selectedTests.ids,
        action: 'change-tags',
        entities: selectedTags,
      });
      setIsProcessing(false);
      props.clearSelectedTests();
      props.onClose();
    } catch (e) {
      setErrorMessage(ERROR_MESSAGES.GENERIC);
      setIsProcessing(false);
    }
  };

  /**
   * processes the changes to athletes
   */
  const processAthleteChanges = async (): Promise<void> => {
    setIsProcessing(!isProcessing);
    setErrorMessage(null);
    try {
      await bulkChangeAthlete(
        selectedAthlete.entity as IAthlete,
        props.selectedTests.tests
      );
      setIsProcessing(false);
      props.updateTests({
        selectedTests: props.selectedTests.ids,
        action: 'change-athlete',
        entities: selectedAthlete.entity as IAthlete,
      });
      props.clearSelectedTests();
      props.onClose();
    } catch (e) {
      setErrorMessage(ERROR_MESSAGES.GENERIC);
      setIsProcessing(false);
    }
  };
  /**
   * @description processes test deletion
   */
  const processTestDeletion = async (): Promise<void> => {
    setIsProcessing(!isProcessing);
    setErrorMessage(null);
    try {
      await bulkDisable(
        props.selectedTests.tests.map((t: ITest): string => t.id)
      );
      setIsProcessing(false);
      props.updateTests({
        selectedTests: props.selectedTests.ids,
        action: 'delete',
      });
      props.clearSelectedTests();
      props.onClose();
    } catch (e) {
      setErrorMessage(ERROR_MESSAGES.GENERIC);
      setIsProcessing(false);
    }
  };

  /**
   * @description processes test activation
   */
  const processTestActivation = async (): Promise<void> => {
    setIsProcessing(!isProcessing);
    setErrorMessage(null);
    try {
      await bulkActivate(
        props.selectedTests.tests.map((t: ITest): string => t.id)
      );
      setIsProcessing(false);
      props.updateTests({
        selectedTests: props.selectedTests.ids,
        action: 'enable',
      });
      props.clearSelectedTests();
      props.onClose();
    } catch (e) {
      console.error(e);
      setErrorMessage(ERROR_MESSAGES.GENERIC);
      setIsProcessing(false);
    }
  };

  return (
    <PopupCard onClose={props.onClose} size='medium' noClickOutside>
      <PopupCardContent>
        <CardHeader surface={8}>
          <CardHeaderLeft>
            <h6>Edit Tests</h6>
          </CardHeaderLeft>
          <CardHeaderRight>
            <CloseButton onClick={props.onClose} />
          </CardHeaderRight>
        </CardHeader>
        {isProcessing && <LinearLoading />}
        <CardBody>
          <Row shouldCollapse>
            <LeftColumn>
              <ListControlLabel label='Selected Test(s)'>
                <Count value={Object.keys(props.selectedTests.ids).length} />
              </ListControlLabel>
              <ListControlContainer>
                <ScrollableArea
                  style={{
                    flex: 1,
                  }}
                >
                  <SelectedTestList tests={props.selectedTests.tests} />
                </ScrollableArea>
              </ListControlContainer>
            </LeftColumn>
            <RightColumn>
              <TabLabels style={{ height: '40px' }}>
                <TabLabel
                  activeElevation={tabsElevation}
                  onClick={(): void => setMode(EDIT_MODE.ATHLETE)}
                  selected={EDIT_MODE.ATHLETE === mode}
                >
                  Change Athlete
                </TabLabel>
                <TabLabel
                  activeElevation={tabsElevation}
                  onClick={(): void => setMode(EDIT_MODE.TAGS)}
                  selected={EDIT_MODE.TAGS === mode}
                >
                  Change Tags
                </TabLabel>
                <TabLabel
                  activeElevation={tabsElevation}
                  onClick={(): void => setMode(EDIT_MODE.DELETE)}
                  selected={EDIT_MODE.DELETE === mode}
                >
                  {props.showingDisabledTests
                    ? 'Enable Tests'
                    : 'Disable Tests'}
                </TabLabel>
              </TabLabels>
              <TabContent>
                {EDIT_MODE.ATHLETE === mode && (
                  <React.Fragment>
                    <ControlLabel>Athlete</ControlLabel>
                    <AthleteSelect
                      value={selectedAthlete}
                      onChange={setSelectedAthlete}
                    />
                  </React.Fragment>
                )}
                {EDIT_MODE.DELETE === mode ? (
                  props.showingDisabledTests ? (
                    <h5>
                      This action will make the tests active in the system and
                      add their contribution to any population (athlete
                      averages, team averages, group averages, etc.) they belong
                      to.
                    </h5>
                  ) : (
                    <h5>
                      Are you sure you want to disable the selected tests? This
                      action will remove their contribution to any population
                      (athlete averages, team averages, group averages, etc.)
                      they belong to.
                    </h5>
                  )
                ) : null}

                {EDIT_MODE.TAGS === mode && (
                  <TagList
                    tags={tags}
                    selectedTags={selectedTags}
                    onSelect={handleTagChange}
                  />
                )}
              </TabContent>
            </RightColumn>
          </Row>
        </CardBody>
        <CardFooter surface={8}>
          <CardFooterLeft>
            {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
          </CardFooterLeft>
          <CardFooterRight>
            {EDIT_MODE.ATHLETE === mode && (
              <Button
                onClick={processAthleteChanges}
                disabled={isProcessing || !selectedAthlete}
                icon='edit'
                title='Change Athlete'
                style={{ whiteSpace: 'nowrap' }}
              />
            )}
            {EDIT_MODE.TAGS === mode && (
              <Button
                onClick={processTagChanges}
                disabled={isProcessing}
                icon='edit'
                title='Change Tags'
                style={{ whiteSpace: 'nowrap' }}
              />
            )}
            {EDIT_MODE.DELETE === mode && (
              <Button
                onClick={
                  props.showingDisabledTests
                    ? processTestActivation
                    : processTestDeletion
                }
                disabled={isProcessing}
                icon={props.showingDisabledTests ? 'check_circle' : 'block'}
                title={
                  props.showingDisabledTests ? 'Enable Tests' : 'Disable Tests'
                }
                style={{ whiteSpace: 'nowrap' }}
              />
            )}
          </CardFooterRight>
        </CardFooter>
      </PopupCardContent>
    </PopupCard>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const LeftColumn = styled(FullHeightColumn as any)`
  @media (max-width: 990px) {
    order: 2;
  }
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const RightColumn = styled(FullHeightColumn as any)`
  padding-top: 20px;
  padding-bottom: 10px;
  padding-left: 10px;
  padding-right: 10px;
  @media (max-width: 990px) {
    order: 1;
  }
`;

const TabContent = styled.div`
  flex: 3;
  background-color: var(--color-surface-${tabsElevation});
  padding: 20px 10px;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
`;

interface IProps extends RouteComponentProps {
  disabled: boolean;
  selectedTests: ISelectedTests;
  clearSelectedTests: () => void;
  count?: number;
  updateTests: (updateToApply: IUpdateInPlace) => void;
  showingDisabledTests: boolean;
}

export const EditTests = withRouter((props: IProps): React.ReactElement => {
  const open = (): void => {
    openPopup(
      <EditTestsPopup
        selectedTests={props.selectedTests}
        clearSelectedTests={props.clearSelectedTests}
        onClose={closePopup(props.history)}
        updateTests={props.updateTests}
        showingDisabledTests={props.showingDisabledTests}
      />,
      props.history,
      'edit-tests'
    );
  };
  return (
    <Button
      icon='edit'
      title='Edit Tests'
      disabled={props.disabled}
      onClick={open}
      style={{ alignItems: 'center', whiteSpace: 'nowrap' }}
    >
      {props.count > 0 && <CountContainer>({props.count})</CountContainer>}
    </Button>
  );
});

const CountContainer = styled.span`
  margin-left: 3px;
`;
